import type { DraggableProvidedDragHandleProps, DropResult } from '@hello-pangea/dnd';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import type { FunctionComponent } from 'react';
import { createPortal } from 'react-dom';

import type { ProfileTaskSummary } from '../model/profile-task';
import { ProfileTaskReorder } from './ProfileTaskReorder';
import { ProfileTaskRowTitle } from './ProfileTaskRowTitle';

const TaskRow: FunctionComponent<{
    readonly dragHandleProps: DraggableProvidedDragHandleProps | null;
    readonly index: number;
    readonly isEditing: boolean;
    readonly isDragging: boolean;
    readonly isSelected: boolean;
    readonly onOpen: () => void;
    readonly onMove: (nextIndex: number) => void;
    readonly onRemove: (() => void) | undefined;
    readonly summary: ProfileTaskSummary;
    readonly total: number;
}> = ({ dragHandleProps, index, isDragging, isEditing, isSelected, onMove, onOpen, onRemove, summary, total }) => (
    <div
        className={`-ml-m flex w-[calc(100%+var(--spacing-m))] items-center gap-xs rounded-md px-s py-xxs ${isDragging ? 'surface-floating-white' : ''}`}
        data-dragging={isDragging}
        data-selected={isSelected}
        data-testid="profile-task-row"
    >
        {isEditing ? (
            <ProfileTaskReorder dragHandleProps={dragHandleProps} index={index} total={total} onMove={onMove} />
        ) : null}
        {isEditing ? null : (
            <span aria-hidden={true} className="select-none px-s">
                •
            </span>
        )}
        <ProfileTaskRowTitle
            isEditing={isEditing}
            isSelected={isSelected}
            summary={summary}
            onOpen={onOpen}
            onRemove={onRemove}
        />
    </div>
);

/** A profile's task rows, reordered by drag or arrow keys and removed from each row's menu while editing. */
export const ProfileTaskSortableList: FunctionComponent<{
    /** Which rows offer removal; all by default. */
    readonly canRemove?: (summary: ProfileTaskSummary) => boolean;
    readonly isEditing: boolean;
    readonly isSelected?: (summary: ProfileTaskSummary) => boolean;
    readonly onMove: (fromIndex: number, toIndex: number) => void;
    readonly onOpen: (summary: ProfileTaskSummary, index: number) => void;
    readonly onRemove: (summary: ProfileTaskSummary) => void;
    readonly summaries: readonly ProfileTaskSummary[];
}> = ({ canRemove, isEditing, isSelected, onMove, onOpen, onRemove, summaries }) => {
    const handleDragEnd = ({ destination, source }: DropResult): void => {
        if (destination === null) {
            return;
        }
        onMove(source.index, destination.index);
    };

    return (
        <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="profile-tasks" isDropDisabled={!isEditing}>
                {({ droppableProps, innerRef, placeholder }) => (
                    <ul ref={innerRef} className="m-0 flex list-none flex-col p-0" {...droppableProps}>
                        {summaries.map((summary, index) => (
                            <Draggable
                                key={summary.taskId}
                                draggableId={summary.taskId}
                                index={index}
                                isDragDisabled={!isEditing}
                            >
                                {({ draggableProps, dragHandleProps, innerRef: draggableRef }, snapshot) => {
                                    const taskRow = (
                                        <li ref={draggableRef} className="list-none" {...draggableProps}>
                                            <TaskRow
                                                dragHandleProps={dragHandleProps}
                                                index={index}
                                                isDragging={snapshot.isDragging}
                                                isEditing={isEditing}
                                                isSelected={isSelected?.(summary) ?? false}
                                                summary={summary}
                                                total={summaries.length}
                                                onMove={(nextIndex) => onMove(index, nextIndex)}
                                                onOpen={() => onOpen(summary, index)}
                                                onRemove={
                                                    canRemove?.(summary) === false ? undefined : () => onRemove(summary)
                                                }
                                            />
                                        </li>
                                    );

                                    return snapshot.isDragging ? createPortal(taskRow, document.body) : taskRow;
                                }}
                            </Draggable>
                        ))}
                        {placeholder}
                    </ul>
                )}
            </Droppable>
        </DragDropContext>
    );
};
